import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { getStatusBadgeClass, getStatusLabel } from '@/lib/utils';
import { Plus, Eye, Pencil, Trash2, X } from 'lucide-react';
import { permissions, ROLES } from '@/lib/permissions';
import { getDashboardMetric } from '@shared/dashboard-metrics';
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';

const STATUS_LIST = ['DRAFT', 'PENDING_MANAGER_APPROVAL', 'MANAGER_APPROVED', 'REJECTED_BY_MANAGER', 'APPROVED', 'REJECTED_BY_CLUSTER_HEAD'];
const PAGE_SIZE = 20;

export default function ActualStatusListPage() {
  const { hasRole, user } = useAuth();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  // Initialize filters from URL params so dashboard drill-downs land on a
  // page that actually shows the filtered data.
  const metricKey = searchParams.get('metric') || '';
  const initialMetric = getDashboardMetric(metricKey) || undefined;
  const [records, setRecords] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [projectFilter, setProjectFilter] = useState(searchParams.get('projectId') || 'all');
  const [dateFrom, setDateFrom] = useState(searchParams.get('dateFrom') || '');
  const [dateTo, setDateTo] = useState(searchParams.get('dateTo') || '');
  const [metric, setMetric] = useState(initialMetric);
  const [page, setPage] = useState(parseInt(searchParams.get('page') || '1') || 1);
  const [projects, setProjects] = useState<any[]>([]);

  // DRAFT is only meaningful for the creator (Engineer) and Admins. Manager,
  // Cluster Head and Viewer are never given drafts, so the option is omitted.
  const canSeeDrafts =
    hasRole(ROLES.SITE_ENGINEER) || hasRole(ROLES.ORG_ADMIN) || hasRole(ROLES.SUPER_ADMIN);
  const statusOptions = STATUS_LIST.filter(s => canSeeDrafts || s !== 'DRAFT');

  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (recordId: string) => api.delete(`/actual-status/${recordId}`),
    onSuccess: () => {
      setDeleteTargetId(null);
      addToast('Draft deleted successfully.', 'success');
      queryClient.invalidateQueries({ queryKey: ['actual-status'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setReloadKey(k => k + 1);
    },
    onError: (err: any) => {
      setDeleteTargetId(null);
      addToast(err.message || 'Failed to delete', 'error');
    },
  });

  useEffect(() => {
    api.get<{ data: any[] }>('/projects').then(r => setProjects(r.data || []));
  }, []);

  useEffect(() => { loadData(); }, [statusFilter, projectFilter, dateFrom, dateTo, metric?.key, page, reloadKey]);

  const validDateRange = !dateFrom || !dateTo || dateFrom <= dateTo;

  // Old URLs may still carry the removed search/type/skill params. They are
  // ignored (never forwarded to the API) and stripped from the URL whenever a
  // supported filter is updated.
  const stripRemovedParams = () => {
    const next = new URLSearchParams(searchParams);
    for (const key of ['search', 'labourType', 'skillLevel']) next.delete(key);
    setSearchParams(next, { replace: true });
  };

  // Any manual filter change overrides the dashboard snapshot view.
  const applyManualChange = (fn: (v: string) => void) => (value: string) => {
    fn(value);
    setMetric(undefined);
    setPage(1);
    stripRemovedParams();
  };

  const clearMetric = () => {
    setMetric(undefined);
    setStatusFilter('all');
    setPage(1);
    stripRemovedParams();
  };

  async function loadData() {
    setLoading(true);
    setLoadFailed(false);
    try {
      if (!validDateRange) { setRecords([]); setTotal(0); setLoading(false); return; }
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      if (projectFilter && projectFilter !== 'all') params.set('projectId', projectFilter);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      if (metric) params.set('metric', metric.key);
      params.set('page', String(page));
      params.set('limit', String(PAGE_SIZE));
      const res = await api.get<{ data: any[]; pagination?: { total: number } }>(`/actual-status?${params.toString()}`);
      setRecords(res.data || []);
      setTotal(res.pagination?.total ?? (res.data ?? []).length);
    } catch (e) {
      console.error(e);
      setRecords([]);
      setTotal(0);
      setLoadFailed(true);
    }
    setLoading(false);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold text-primary">Actual Labour</h1>
        {hasRole(ROLES.SITE_ENGINEER) && (
          <Link to="/actuals/new">
            <Button><Plus size={16} className="mr-2" />New Actual Labour</Button>
          </Link>
        )}
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <Select value={statusFilter} onValueChange={applyManualChange(setStatusFilter)}>
              <SelectTrigger aria-label="Status filter"><SelectValue placeholder="All Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {statusOptions.map(s => <SelectItem key={s} value={s}>{getStatusLabel(s)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={projectFilter} onValueChange={applyManualChange(setProjectFilter)}>
              <SelectTrigger aria-label="Project filter"><SelectValue placeholder="All Projects" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map((p: any) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setMetric(undefined); setPage(1); stripRemovedParams(); }} aria-label="Start date" />
            <Input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setMetric(undefined); setPage(1); stripRemovedParams(); }} aria-label="End date" />
          </div>
          {!validDateRange && (
            <p className="text-error text-sm mt-2">Start date cannot be after end date.</p>
          )}
        </CardContent>
      </Card>

      {metric && (
        <div className="flex items-center gap-2 px-1">
          <Badge variant="info">Filtered: {metric.label}</Badge>
          <Button variant="ghost" size="sm" onClick={clearMetric} aria-label="Clear dashboard filter">
            <X size={14} className="mr-1" /> Clear filter
          </Button>
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="space-y-3 p-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-12 rounded-lg bg-surface-secondary animate-pulse" />
              ))}
            </div>
          ) : loadFailed ? (
            <div className="text-center py-8">
              <p className="text-secondary">Unable to load Actual Labour records.</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => setReloadKey(k => k + 1)}>Retry</Button>
            </div>
          ) : records.length === 0 ? (
            <p className="text-center py-8 text-secondary">No Actual Labour records found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-secondary">Project</th>
                    <th className="text-left py-3 px-4 font-medium text-secondary">Block</th>
                    <th className="text-left py-3 px-4 font-medium text-secondary">Work Date</th>
                    <th className="text-left py-3 px-4 font-medium text-secondary">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-secondary">Created By</th>
                    <th className="text-right py-3 px-4 font-medium text-secondary">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map((r: any) => (
                    <tr key={r.id} className="border-b hover:bg-hover transition-colors">
                      <td className="py-3 px-4">{r.project_name}</td>
                      <td className="py-3 px-4">{r.block_name || '-'}</td>
                      <td className="py-3 px-4">{r.work_date}</td>
                      <td className="py-3 px-4">
                        <Badge variant={getStatusBadgeClass(r.status)}>{getStatusLabel(r.status)}</Badge>
                      </td>
                      <td className="py-3 px-4">{r.created_by_name}</td>
                      <td className="py-3 px-4 text-right">
                        <div className="flex justify-end gap-1">
                          <Link to={`/actuals/${r.id}`}>
                            <Button variant="ghost" size="sm" title="View actual status"><Eye size={14} /></Button>
                          </Link>
                          {permissions.canEditOperationalRecord(user?.roles ?? [], { status: r.status, createdBy: r.created_by }, user?.id) && (
                            <Link to={`/actuals/${r.id}/edit`}>
                              <Button variant="ghost" size="sm" title="Edit actual status"><Pencil size={14} /></Button>
                            </Link>
                          )}
                          {permissions.canDeleteOperationalRecord(user?.roles ?? [], { status: r.status, createdBy: r.created_by }, user?.id) && (
                            <Button variant="ghost" size="sm" title="Delete draft" onClick={() => setDeleteTargetId(r.id)} disabled={deleteMutation.isPending}>
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
        </CardContent>
      </Card>

      <AlertDialog open={deleteTargetId !== null} onOpenChange={(open) => { if (!open && !deleteMutation.isPending) setDeleteTargetId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete draft?</AlertDialogTitle>
            <AlertDialogDescription>This Actual Labour draft will be permanently deleted. This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <button
              type="button"
              className="inline-flex items-center justify-center rounded-md text-sm font-medium h-10 px-4 py-2 bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:pointer-events-none disabled:opacity-50"
              disabled={deleteMutation.isPending}
              onClick={() => { if (deleteTargetId) deleteMutation.mutate(deleteTargetId); }}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Draft'}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
